/**
 * Broadcast event bus — diffs successive `KothRound` snapshots (+ an on-chain
 * countdown) into a typed event stream that drives the /watch camera director
 * and, later, the TTS narrator. ONE source of truth for "something just
 * happened in the round" so cuts, captions and play-by-play all agree.
 *
 * Emits at most one event per poll tick, picking the highest-signal change
 * (settled > king change > pot spike > touch). Endgame thresholds are derived
 * from a chain-anchored clock (useChainNow), not Date.now() — surfpool's Clock
 * drifts ~109min ahead of host wall-clock, so a wall-clock countdown would fire
 * the endgame cut at the wrong moment.
 */
import { useEffect, useRef, useState } from "react";

import type { KothRoundState } from "../games/last-touch/useKothRound";
import { useChainNow } from "../util/useChainNow";

export type BroadcastEvent =
  | { kind: "king_change"; king: string }
  | { kind: "pot_spike"; potLamports: bigint; deltaLamports: bigint }
  | { kind: "touch"; touchCount: number }
  | { kind: "endgame"; secsLeft: number }
  | { kind: "settled"; winner: string; finalPotLamports: bigint };

/** Wrapper so identical consecutive events still re-trigger downstream effects. */
export interface BroadcastEventEnvelope {
  seq: number;
  event: BroadcastEvent;
}

export function useBroadcastEvents(
  round: KothRoundState | null,
): BroadcastEventEnvelope | null {
  const chainNow = useChainNow(1000);
  const [envelope, setEnvelope] = useState<BroadcastEventEnvelope | null>(null);

  const seqRef = useRef(0);
  const roundKeyRef = useRef<string | null>(null);
  const prevPotRef = useRef<bigint | null>(null);
  const prevKingRef = useRef<string | null>(null);
  const prevTouchRef = useRef<number | null>(null);
  const prevStatusRef = useRef<number | null>(null);
  const endgameFiredRef = useRef<Set<number>>(new Set());

  const emit = (event: BroadcastEvent): void => {
    seqRef.current += 1;
    setEnvelope({ seq: seqRef.current, event });
  };

  // Diff round-account fields whenever a fresh snapshot lands.
  useEffect(() => {
    if (!round) return;
    const roundKey = round.roundId.toString();

    // New round (or first sight): seed baselines, emit nothing.
    if (roundKeyRef.current !== roundKey) {
      roundKeyRef.current = roundKey;
      prevPotRef.current = BigInt(round.potLamports.toString());
      prevKingRef.current = round.currentKing.toBase58();
      prevTouchRef.current = round.touchCount;
      prevStatusRef.current = round.status;
      endgameFiredRef.current = new Set();
      return;
    }

    const pot = BigInt(round.potLamports.toString());
    const king = round.currentKing.toBase58();
    const touch = round.touchCount;
    const status = round.status;

    // One event per tick, highest signal wins.
    let next: BroadcastEvent | null = null;
    if (prevStatusRef.current !== 1 && status === 1) {
      next = { kind: "settled", winner: king, finalPotLamports: pot };
    } else if (prevKingRef.current && king !== prevKingRef.current) {
      next = { kind: "king_change", king };
    } else if (prevPotRef.current != null && pot > prevPotRef.current) {
      next = {
        kind: "pot_spike",
        potLamports: pot,
        deltaLamports: pot - prevPotRef.current,
      };
    } else if (prevTouchRef.current != null && touch > prevTouchRef.current) {
      next = { kind: "touch", touchCount: touch };
    }

    prevPotRef.current = pot;
    prevKingRef.current = king;
    prevTouchRef.current = touch;
    prevStatusRef.current = status;

    if (next) emit(next);
  }, [round]);

  // Endgame thresholds, fired once each as the chain clock ticks down. Reset
  // when a touch extends the round back above the window so a re-entry re-arms.
  useEffect(() => {
    if (!round || round.status === 1) return;
    const secsLeft = Number(round.expiresAt.toString()) - chainNow;
    if (secsLeft > 15) {
      endgameFiredRef.current.clear();
      return;
    }
    if (secsLeft <= 10 && secsLeft > 5 && !endgameFiredRef.current.has(10)) {
      endgameFiredRef.current.add(10);
      emit({ kind: "endgame", secsLeft: 10 });
    }
    if (secsLeft <= 5 && secsLeft > 0 && !endgameFiredRef.current.has(5)) {
      endgameFiredRef.current.add(5);
      emit({ kind: "endgame", secsLeft: 5 });
    }
  }, [chainNow, round]);

  return envelope;
}
